<template>
  <div>
    <v-row class="px-4 mb-2" align="center" justify="start">
      <v-col cols="12" md="4" class="text-center">
        <RowsPerPage :pagination="pagination" :disabled="isLoading" />
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="cashRegisters"
      :items-per-page="pagination.per_page"
      :loading="isLoading"
      :loading-text="$t('components.noData.fetchingEntries')"
      hide-default-footer>
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>
            {{ $t('entities.cashRegisters.title') }}
          </v-toolbar-title>
          <v-divider inset vertical class="mx-4" />
          <small class="mr-2">{{ pagination.total }}</small>
          <RefreshButton :refresh="refresh" :loading="isLoading" />
          <v-spacer />
          <v-btn
            v-if="canUpsertCashRegister"
            @click="openUpsertDialog()"
            :disabled="isLoading"
            color="primary">
            {{ $t('miscellaneous.create') }}
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          @click="openUpsertDialog(item)"
          depressed
          x-small
          dark
          fab
          color="primary"
          class="glow ml-2">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          @click="openDeleteDialog(item)"
          depressed
          x-small
          dark
          fab
          color="error"
          class="ml-2 glow">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <DataTableFooter :loading="isLoading" :pagination="pagination" />
    <UpsertDialog
      v-if="canUpsertCashRegister"
      @cash-register:upsert="upsertCashRegister"
      @dialog:close="closeUpsertDialog"
      :cash-register="cashRegisterToUpsert"
      :is-loading="isLoading"
      :is-opened="isUpsertDialogOpen" />
    <DeleteDialog
      v-if="canDeleteCashRegister"
      @delete:confirm="deleteCashRegister"
      @dialog:close="closeDeleteDialog"
      :cash-register="cashRegisterToDelete"
      :is-loading="isLoading"
      :is-opened="isDeleteDialogOpen" />
  </div>
</template>

<script>
import CashRegister from '@/models/shop/CashRegister'
import DeleteDialog from './DeleteDialog.vue'
import DataTableFooter from '@/components/crud/DataTableFooter.vue'
import RowsPerPage from '@/components/crud/RowsPerPage.vue'
import isRole from '@/mixins/crud/computed/isRole'
import { paginationWatch } from '@/mixins/crud/watch/pagination.js'
import RefreshButton from '@/components/crud/RefreshButton.vue'
import UpsertDialog from './UpsertDialog.vue'

const resetPagination = () => ({
  search: '',
  current_page: 1,
  from: 1,
  last_page: 0,
  path: '',
  per_page: 10,
  to: 0,
  total: 0,
})

export default {
  name: 'CashRegisters',
  components: {
    DataTableFooter,
    RowsPerPage,
    RefreshButton,
    DeleteDialog,
    UpsertDialog,
  },
  mixins: [paginationWatch, isRole],
  data() {
    return {
      cashRegisters: [],
      cashRegisterToDelete: {},
      cashRegisterToUpsert: {},
      isLoading: false,
      isDeleteDialogOpen: false,
      isUpsertDialogOpen: false,
      options: {}, // paginationWatch mixin is dependent on this, but it is not used  for anything. It just has to exist.
      pagination: resetPagination(),
      selectedSearchAttributes: [],
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena ? this.$store.getters.arena.id : null
    },
    canUpsertCashRegister() {
      return this.isAdmin || this.isManager
    },
    canDeleteCashRegister() {
      return this.isAdmin || this.isManager
    },
    headers() {
      const headers = [
        { text: this.$t('generalAttributes.name'), value: 'name' },
        {
          text: this.$t('entities.cashRegisters.attributes.label'),
          value: 'label',
        },
      ]
      if (this.canUpsertCashRegister || this.canDeleteCashRegister) {
        headers.push({
          text: this.$t('miscellaneous.actions'),
          value: 'actions',
          sortable: false,
        })
      }
      return headers
    },
  },
  methods: {
    closeDeleteDialog() {
      this.isDeleteDialogOpen = false
      this.cashRegisterToDelete = {}
    },
    closeUpsertDialog() {
      this.isUpsertDialogOpen = false
      this.cashRegisterToUpsert = {}
    },
    async deleteCashRegister() {
      if (!this.canDeleteCashRegister) return
      this.isLoading = true
      try {
        await CashRegister.api.delete(
          this.arenaId,
          this.cashRegisterToDelete.id,
        )
        await this.fetchEntities()
        this.closeDeleteDialog()
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    async fetchEntities(append = this.setParams()) {
      try {
        this.isLoading = true
        const response = await CashRegister.api.list(this.arenaId, append)
        const data = response.data
        this.cashRegisters = data.data.map((it) => new CashRegister(it))
        this.updatePagination(data.metadata || data.meta, data.data.length)
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    openDeleteDialog(cashRegister) {
      this.cashRegisterToDelete = cashRegister
      this.isDeleteDialogOpen = true
    },
    openUpsertDialog(cashRegister = null) {
      this.cashRegisterToUpsert = cashRegister ? { ...cashRegister } : {}
      this.isUpsertDialogOpen = true
    },
    refresh() {
      this.pagination = resetPagination()
    },
    sanitizePayload(payload) {
      const { id, ...payloadWithoutId } = payload
      return id ? { id, ...payloadWithoutId } : payloadWithoutId
    },
    updatePagination(metadata = null, dataLength = 0) {
      const page = metadata?.page ?? this.pagination.current_page ?? 1
      const size = this.pagination.per_page ?? metadata?.size ?? 10
      const total = metadata?.count ?? metadata?.total ?? dataLength
      const lastPage = size ? Math.ceil((total || 0) / size) : 0
      const from = total ? (page - 1) * size + 1 : 0
      const to = total ? Math.min(from + size - 1, total) : 0
      this.pagination.current_page = page
      this.pagination.total = total
      this.pagination.last_page = lastPage
      this.pagination.from = from
      this.pagination.to = to
    },
    async upsertCashRegister(payload) {
      if (!this.canUpsertCashRegister) return
      this.isLoading = true
      const requestPayload = this.sanitizePayload(payload)
      try {
        if (!requestPayload.id)
          await CashRegister.api.create(this.arenaId, payload)
        else await CashRegister.api.update(this.arenaId, payload.id, payload)
        await this.fetchEntities()
        return this.closeUpsertDialog()
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
  },
  mounted() {
    return this.fetchEntities()
  },
}
</script>
